import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../../firebase-config";
import { doc, getDoc, updateDoc } from "firebase/firestore";

const ProjectView = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const currentUser = auth.currentUser;
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;

    const fetchProject = async () => {
      try {
        const projectRef = doc(db, "projects", projectId);
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
          setProject(projectSnap.data());
        } else {
          setError("Project not found.");
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        setError("Failed to load project.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [currentUser, projectId]);

  // Handle input changes but only save on Save button click
  const handleFieldChange = (field, value) => {
    setProject((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveChanges = async () => {
    if (!project || project.status === "submitted") return; // Prevent saving after submission

    try {
      setIsSaving(true);
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        title: project.title,
        description: project.description,
        budget: parseFloat(project.budget),
        requirements: project.requirements.split(",").map((req) => req.trim()),
      });
      alert("Changes saved!");
    } catch (error) {
      console.error("Error saving project:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitProject = async () => {
    if (!project) return;
    setIsSubmitting(true);

    try {
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, { status: "submitted" });

      setProject((prev) => ({ ...prev, status: "submitted" }));
      alert("Project submitted successfully!");
      navigate("/projects");
    } catch (error) {
      console.error("Error submitting project:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!project) return <Typography>No project found.</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        {project.title}
      </Typography>

      <TextField
        label="Title"
        fullWidth
        value={project.title}
        onChange={(e) => handleFieldChange("title", e.target.value)}
        sx={{ mb: 2 }}
        disabled={project.status === "submitted"}
      />

      <TextField
        label="Description"
        fullWidth
        multiline
        rows={4}
        value={project.description}
        onChange={(e) => handleFieldChange("description", e.target.value)}
        sx={{ mb: 2 }}
        disabled={project.status === "submitted"}
      />

      <TextField
        label="Budget ($)"
        fullWidth
        type="number"
        value={project.budget}
        onChange={(e) => handleFieldChange("budget", e.target.value)}
        sx={{ mb: 2 }}
        disabled={project.status === "submitted"}
      />

      <TextField
        label="Requirements (comma-separated)"
        fullWidth
        value={project.requirements}
        onChange={(e) => handleFieldChange("requirements", e.target.value)}
        sx={{ mb: 2 }}
        disabled={project.status === "submitted"}
      />

      <Box mt={3} sx={{ display: "flex", gap: 2 }}>
        {project.status !== "submitted" && (
          <>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSaveChanges}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitProject}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Project"}
            </Button>
          </>
        )}

        {project.status === "submitted" && (
          <Typography variant="body1" color="textSecondary">
            This project has been submitted and can no longer be edited.
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default ProjectView;
